import { faLock } from "@fortawesome/pro-solid-svg-icons";
import {
  Button,
  Grid,
  Icon,
  Input,
  Message,
  TextArea,
  Tooltip,
} from "@intility/bifrost-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useCreatePlatform } from "../../../api/platforms";
import { PageLayout } from "../../../components/layout/PageLayout";
import type { LogoSelection } from "../../../components/platforms/PlatformLogoPicker";
import {
  logoSelectionToRequest,
  PlatformLogoPicker,
} from "../../../components/platforms/PlatformLogoPicker";
import {
  PlatformThemePicker,
  themeSwatchClass,
} from "../../../components/platforms/PlatformThemePicker";
import { slugify } from "../../../lib/clientTypes";
import { builtinTheme, type ThemeChoice } from "../../../lib/platformIdentity";
import "./RegisterPlatform.css";

// Copied from Intility.Authorization.Contracts SlugRule (#1140): lowercase letters, digits and
// hyphens, starting with a letter, no trailing hyphen, at most 63 characters.
// The Portal cannot reference the .NET package, so a copy is the design.
const PLATFORM_SLUG_PATTERN = /^[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function validateName(value: string): string | null {
  if (!value.trim()) return "Display name is required";
  if (value.trim().length > 100)
    return "Display name must be 100 characters or fewer";
  return null;
}

function validateSlug(value: string): string | null {
  if (!value) return "Slug is required";
  if (!PLATFORM_SLUG_PATTERN.test(value))
    return "Use lowercase letters, digits and hyphens, start with a letter, do not end with a hyphen, and keep it to 63 characters or fewer";
  return null;
}

export default function RegisterPlatform() {
  const navigate = useNavigate();
  const createPlatform = useCreatePlatform();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // A slug the user typed is kept: editing the name must not overwrite it.
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [logoSelection, setLogoSelection] = useState<LogoSelection>({
    type: "none",
  });
  // The theme defaults to neutral, the standard Portal look.
  const [themeChoice, setThemeChoice] = useState<ThemeChoice>("neutral");

  // A builtin logo carries its theme: while one is selected, the theme
  // follows the logo and cannot be neutral.
  const forcedTheme =
    logoSelection.type === "builtin"
      ? builtinTheme(logoSelection.key)
      : undefined;
  const effectiveTheme: ThemeChoice = forcedTheme ?? themeChoice;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const error = validateName(name);
    const slugProblem = validateSlug(slug);
    setNameError(error);
    setSlugError(slugProblem);
    if (error || slugProblem) return;

    try {
      const platform = await createPlatform.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        slug,
        logo: logoSelectionToRequest(logoSelection),
        theme: effectiveTheme,
      });
      // ADR-0036 gaps come only from the create response, passed in router
      // state and shown on the first landing, never re-fetched.
      navigate(`/platforms/${platform.id}`, {
        state: {
          gaps:
            platform.gaps && platform.gaps.length > 0
              ? platform.gaps
              : undefined,
        },
      });
    } catch {
      // Error shown via createPlatform.error
    }
  }

  return (
    <PageLayout
      title="Register a new platform"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Platforms", to: "/platforms" },
        { label: "Register" },
      ]}
      description="A platform groups the clients your service needs and has one blueprint. You own the platform, and ownership cascades to every client in it. Add clients on the platform page after you create the platform."
    >
      <div className="register-platform-container">
        <Grid gap={24}>
          {createPlatform.error && (
            <Message state="warning" header="Registration failed">
              {createPlatform.error instanceof Error
                ? createPlatform.error.message
                : "An unexpected error occurred."}
            </Message>
          )}
          <form onSubmit={handleSubmit}>
            <Grid gap={16}>
              <Input
                label="Display name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(validateName(e.target.value));
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
                state={nameError ? "alert" : "default"}
                feedback={nameError ?? undefined}
                required
                placeholder="e.g. Identity Manager"
              />
              <Input
                label="Slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                  if (slugError) setSlugError(validateSlug(e.target.value));
                }}
                state={slugError ? "alert" : "default"}
                feedback={
                  slugError ??
                  "The slug is permanent. You cannot change it later. The slug is used in authorization tuples. Make it descriptive but short."
                }
                required
                placeholder="e.g. identity-manager"
              />
              <TextArea
                label="Description"
                optional
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe what this platform does"
                rows={3}
              />

              <fieldset className="register-platform-field">
                <legend className="bf-label">Logo (optional)</legend>
                <p className="bf-small register-platform-field-hint">
                  The logo shows beside the platform name on the platform page.
                </p>
                <PlatformLogoPicker
                  value={logoSelection}
                  onChange={setLogoSelection}
                />
              </fieldset>

              <fieldset className="register-platform-field">
                <legend className="bf-label">Theme (optional)</legend>
                <p className="bf-small register-platform-field-hint">
                  The theme sets the accent color of the platform page. The
                  default is neutral.
                </p>
                {forcedTheme ? (
                  <Tooltip content="The theme follows the service logo.">
                    <span
                      role="img"
                      className="register-platform-theme-locked"
                      aria-label="The theme follows the service logo."
                    >
                      <span className={themeSwatchClass(effectiveTheme)} />
                      <Icon icon={faLock} />
                    </span>
                  </Tooltip>
                ) : (
                  <PlatformThemePicker
                    value={effectiveTheme}
                    onChange={setThemeChoice}
                  />
                )}
              </fieldset>

              <div>
                <Button type="submit" disabled={createPlatform.isPending}>
                  {createPlatform.isPending ? "Creating..." : "Create platform"}
                </Button>
              </div>
            </Grid>
          </form>
        </Grid>
      </div>
    </PageLayout>
  );
}
